/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import get from "lodash/get";
import { detectComponent, isEmpty, getCollectionData } from "../../../utils/utils";
import { AuthorWithTime } from "../../Atoms/AuthorWithTimestamp";
import { CollectionName } from "../../Atoms/CollectionName";
import { Headline } from "../../Atoms/Headline";
import { HeroImage } from "../../Atoms/HeroImage";
import { SectionTag } from "../../Atoms/SectionTag";
import { StoryCard } from "../../Molecules/StoryCard";
import { StateProvider } from "../../SharedContext";
import "./aside-collection.m.css";

const StoryCollection = ({ data, slotData, horizontal, config }) => {
  const { theme = "", title = "", collectionSlug = "" } = slotData || config;

  const heading = title || "Related Stories";
  const isHorizontal = horizontal ? "horizontal-wrapper" : "";

  const [storyList, updateStoryList] = useState([]);
  const [collectionData, updateCollectionData] = useState([]);
  const mountAt = get(config, ["mountAtPrefix"], "");

  const updateStoryListItems = async slug => {
    const collectionDataResponse = await getCollectionData(slug, mountAt);
    updateCollectionData(collectionDataResponse);
    const storyItems = collectionDataResponse.items;
    updateStoryList(storyItems);
  };

  useEffect(() => {
    if (collectionSlug) {
      updateStoryListItems(collectionSlug);
    }
  }, []);

  useEffect(() => {
    if (!collectionSlug && data) {
      const storyItems = data.items || data;
      updateStoryList(storyItems);
    }
  }, [data]);

  return (
    <>
      <CollectionName customCollectionName={heading} textColor collection={collectionData} />
      <div styleName="stories">
        {storyList && storyList.length > 0 ? (
          storyList.slice(0, 4).map(item => {
            const story = item.story || item;
            return (
              <div styleName="story" key={story.id}>
                <StoryCard story={story} isHorizontal theme={theme}>
                  <div styleName="story-hero-image">
                    <HeroImage story={story} aspectRatio={[[4, 3]]} />
                  </div>
                  <div>
                    {isHorizontal && <SectionTag story={story} />}
                    <Headline story={story} />
                    <div styleName="story-byline">
                      <AuthorWithTime story={story} />
                    </div>
                  </div>
                </StoryCard>
              </div>
            );
          })
        ) : (
          <div data-test-id="no-stories" styleName="info">
            No stories found.
          </div>
        )}
      </div>
    </>
  );
};

StoryCollection.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  config: PropTypes.object,
  horizontal: PropTypes.bool,
  slotData: PropTypes.object
};

const AsideCollection = ({ data = {}, config = {}, horizontal = false, slots = [], adComponent, widgetComp }) => {
  if (isEmpty(data)) return null;
  const { theme } = config;
  const isHorizontal = horizontal ? "horizontal-wrapper" : "";

  if (horizontal) {
    return (
      <div
        className="arrow-component arr--aside-collection"
        styleName={`wrapper ${isHorizontal}`}
        style={{ backgroundColor: theme }}
        data-test-id="aside-collection"
      >
        <StoryCollection data={data} horizontal={horizontal} config={config} />
      </div>
    );
  }

  const asideSlot = (type, slot) => {
    switch (type) {
      case "ad":
        return (
          <div styleName="ad-slot" className="aside-collection-ad-slot">
            {detectComponent(slot, adComponent, widgetComp)}
          </div>
        );
      case "widget":
        return (
          <div styleName="ad-slot" className="aside-collection-ad-slot">
            {detectComponent(slot, adComponent, widgetComp)}
          </div>
        );

      case "collection":
        return <StoryCollection slotData={slot} data={data} horizontal={horizontal} config={config} />;
    }
  };

  return (
    <div
      className="arrow-component arr--aside-collection"
      styleName={`wrapper ${isHorizontal}`}
      style={{ backgroundColor: theme }}
      data-test-id="aside-collection"
    >
      {slots.map(slot => asideSlot(slot.type, slot))}
    </div>
  );
};

AsideCollection.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  config: PropTypes.object,
  horizontal: PropTypes.bool,
  slots: PropTypes.array,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

export default StateProvider(AsideCollection);
